// SITROOM-branded login page — dark theme, monospace details.
// Iter32: two-phase 2FA login (password → 6-digit TOTP / recovery code).
// Iter36: top-right language picker + i18n strings.
import React, { useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { LanguageSelector } from "../components/LanguageSelector";

export default function Login() {
  const { user, login, verify2fa } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // 2FA state
  const [tempToken, setTempToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const otpRef = useRef(null);

  // Already signed in → bounce to dashboard
  if (user) return <Navigate to="/" replace />;

  const goAfter = () => {
    const dest = (location.state && location.state.from) || "/";
    navigate(dest, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError(t("login.errors.required"));
      return;
    }
    setBusy(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.requires_2fa) {
        setTempToken(result.temp_token);
        setOtp("");
        setUseRecovery(false);
        setTimeout(() => otpRef.current?.focus(), 100);
      } else {
        goAfter();
      }
    } catch (err) {
      const detail =
        (err && err.response && err.response.data && err.response.data.detail) ||
        t("login.errors.generic");
      setError(typeof detail === "string" ? detail : t("login.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  // ── Demo one-click login (VNC-friendly — no @ or special chars to type).
  // Visible in preview environments only; hidden on the production host so
  // it never ships to real users. Toggle via env if you need it elsewhere.
  const isProdHost = typeof window !== "undefined"
    && (window.location.hostname === "app.sitroom.eu" || window.location.hostname.endsWith(".sitroom.eu"));
  const showDemoButtons = !isProdHost;
  const demoLogin = async (demoEmail, demoPw) => {
    setError("");
    setBusy(true);
    try {
      const result = await login(demoEmail, demoPw);
      if (result?.requires_2fa) {
        setTempToken(result.temp_token);
      } else {
        goAfter();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || t("login.errors.generic");
      setError(typeof detail === "string" ? detail : t("login.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const submit2fa = async (codeOverride) => {
    setError("");
    const code = (codeOverride ?? otp).trim();
    if (!code) { setError(t("login.errors.enter_code")); return; }
    setBusy(true);
    try {
      await verify2fa(code, tempToken);
      goAfter();
    } catch (err) {
      const detail =
        (err && err.response && err.response.data && err.response.data.detail) ||
        t("login.errors.invalid_code");
      setError(typeof detail === "string" ? detail : t("login.errors.invalid_code"));
      setOtp("");
      setTimeout(() => otpRef.current?.focus(), 60);
    } finally {
      setBusy(false);
    }
  };

  const handleOtpChange = (e) => {
    const v = e.target.value;
    if (useRecovery) { setOtp(v.toUpperCase().slice(0, 32)); return; }
    const clean = v.replace(/\D/g, "").slice(0, 6);
    setOtp(clean);
    if (clean.length === 6 && !busy) {
      // Auto-submit on the 6th digit
      submit2fa(clean);
    }
  };

  const backToPassword = () => {
    setTempToken(null);
    setOtp("");
    setUseRecovery(false);
    setError("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "#08090d",
        backgroundImage:
          "radial-gradient(circle at 20% 10%, rgba(24,95,165,0.20) 0%, transparent 45%), radial-gradient(circle at 80% 90%, rgba(94,163,227,0.10) 0%, transparent 50%)",
      }}
      data-testid="login-page"
    >
      <div className="w-full" style={{ maxWidth: 420 }}>
        {/* Brand */}
        <div className="flex items-center justify-between gap-2 mb-8 select-none">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center"
              style={{
                width: 36, height: 36, background: "#185FA5", borderRadius: 4,
                fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 600,
                color: "#fff", letterSpacing: "0.5px",
              }}
            >S</div>
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "#f0f2f8", lineHeight: 1, letterSpacing: "-0.5px" }}>SITROOM</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "2px", textTransform: "uppercase", marginTop: 4 }}>{t("login.tagline")}</div>
            </div>
          </div>
          {/* Iter36 — language picker (pre-auth) */}
          <LanguageSelector hasUser={false} testIdPrefix="login-language" />
        </div>

        {tempToken ? (
          <div
            className="rounded-lg p-6"
            style={{
              background: "rgba(13,15,24,0.92)",
              border: "1px solid rgba(255,255,255,0.08)",
              backdropFilter: "blur(18px)",
            }}
            data-testid="login-2fa-form"
          >
            <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 600, color: "#f0f2f8", marginBottom: 4, letterSpacing: "-0.3px" }}>
              {t("login.twofa.title")}
            </h1>
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 18 }}>
              {useRecovery ? t("login.twofa.subtitle_recovery") : t("login.twofa.subtitle_otp")}
            </p>
            <input
              ref={otpRef}
              type="text"
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              value={otp}
              onChange={handleOtpChange}
              placeholder={useRecovery ? "ABCDE-12345" : "••••••"}
              className="w-full px-3 py-3 rounded-md outline-none transition-colors"
              style={{
                background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)",
                color: "#f0f2f8", fontFamily: "'DM Mono', monospace",
                fontSize: useRecovery ? 14 : 22,
                letterSpacing: useRecovery ? "1px" : "8px",
                textAlign: "center",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#185FA5")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.10)")}
              data-testid="login-otp-input"
              disabled={busy}
            />
            {error && (
              <div className="mt-4 px-3 py-2 rounded-md" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.30)", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#E24B4A", letterSpacing: "0.3px" }} data-testid="login-error">
                ▸ {error}
              </div>
            )}
            <div className="flex items-center justify-between mt-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.4px" }}>
              <button type="button" onClick={backToPassword} className="transition-colors" style={{ background: "transparent", border: "none", color: "#9ec3ec", cursor: "pointer" }} data-testid="login-2fa-back">
                {t("login.twofa.back")}
              </button>
              <button
                type="button"
                onClick={() => { setUseRecovery((v) => !v); setOtp(""); setError(""); setTimeout(() => otpRef.current?.focus(), 30); }}
                className="transition-colors"
                style={{ background: "transparent", border: "none", color: "#c89adb", cursor: "pointer" }}
                data-testid="login-2fa-toggle-recovery"
              >
                {useRecovery ? t("login.twofa.use_totp") : t("login.twofa.use_recovery")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => submit2fa()}
              disabled={busy || !otp.trim()}
              className="w-full mt-4 py-2 rounded-md"
              style={{
                background: busy || !otp.trim() ? "#0c4076" : "#185FA5",
                border: "1px solid #185FA5", color: "#fff",
                fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 14,
                letterSpacing: "0.5px",
                cursor: busy || !otp.trim() ? "not-allowed" : "pointer",
                opacity: busy ? 0.7 : 1,
              }}
              data-testid="login-2fa-submit-btn"
            >
              {busy ? t("login.twofa.verifying") : t("login.twofa.submit")}
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="rounded-lg p-6"
            style={{
              background: "rgba(13,15,24,0.92)",
              border: "1px solid rgba(255,255,255,0.08)",
              backdropFilter: "blur(18px)",
            }}
            data-testid="login-form"
          >
            <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 24, fontWeight: 600, color: "#f0f2f8", marginBottom: 4, letterSpacing: "-0.3px" }}>{t("login.title")}</h1>
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 24 }}>{t("login.subtitle")}</p>
            <label style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase" }}>{t("login.email")}</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder={t("login.email_placeholder")}
              className="w-full mt-1 mb-4 px-3 py-2 rounded-md outline-none transition-colors"
              style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 13 }}
              onFocus={(e) => (e.target.style.borderColor = "#185FA5")} onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.10)")}
              data-testid="login-email-input"
            />
            <label style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase" }}>{t("login.password")}</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder={t("login.password_placeholder")}
              className="w-full mt-1 px-3 py-2 rounded-md outline-none transition-colors"
              style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)", color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 13 }}
              onFocus={(e) => (e.target.style.borderColor = "#185FA5")} onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.10)")}
              data-testid="login-password-input"
            />
            {error && (
              <div className="mt-4 px-3 py-2 rounded-md" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.30)", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#E24B4A", letterSpacing: "0.3px" }} data-testid="login-error">
                ▸ {error}
              </div>
            )}
            <button
              type="submit" disabled={busy}
              className="w-full mt-6 py-2 rounded-md transition-all"
              style={{ background: busy ? "#0c4076" : "#185FA5", border: "1px solid #185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 14, letterSpacing: "0.5px", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign in →"}
            </button>
            {/* Iter142 — demo one-click login (VNC-friendly). Only rendered
                on non-production hosts. Keeps testers from having to type "@". */}
            {showDemoButtons && (
              <div className="mt-5" data-testid="login-demo-block">
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 8, textAlign: "center" }}>
                  Demo belépés · Időkapu-foglaló
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => demoLogin("gyar@sitroom.hu", "gyar123")}
                    disabled={busy}
                    data-testid="login-demo-plant-btn"
                    style={{
                      background: "rgba(24,95,165,0.15)", border: "1px solid #185FA5", color: "#9ec3ec",
                      borderRadius: 6, padding: "8px 10px", cursor: busy ? "wait" : "pointer",
                      fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase",
                    }}
                  >Gyár (dock_admin)</button>
                  <button
                    type="button"
                    onClick={() => demoLogin("fuvar@sitroom.hu", "fuvar123")}
                    disabled={busy}
                    data-testid="login-demo-fwd-btn"
                    style={{
                      background: "rgba(24,95,165,0.15)", border: "1px solid #185FA5", color: "#9ec3ec",
                      borderRadius: 6, padding: "8px 10px", cursor: busy ? "wait" : "pointer",
                      fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase",
                    }}
                  >Fuvarozó (dock_booker)</button>
                </div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.3px", marginTop: 6, textAlign: "center" }}>
                  gyar123 / fuvar123 — csak betűk és számok
                </div>
              </div>
            )}
            {/* Iter54 — public signup CTA */}
            <div style={{ marginTop: 16, textAlign: "center", fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#9aa1b3", letterSpacing: "0.3px" }}>
              New here?{" "}
              <a href="/signup" style={{ color: "#9ec3ec", fontWeight: 700, textDecoration: "none" }} data-testid="login-signup-link">
                Start free →
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
